import type { Raster } from '@repo/warp-engine';
import { apply, invert, type Matrix } from '../geometry/matrix';
import { blendPixel } from './composite';

export interface ClipRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** 이웃 좌표를 원본 안으로 클램프해 premultiplied 로 읽는다(가장자리 연장). */
const samplePremul = (src: Raster, x: number, y: number, out: Float64Array): void => {
  const cx = Math.min(src.width - 1, Math.max(0, x));
  const cy = Math.min(src.height - 1, Math.max(0, y));
  const o = (cy * src.width + cx) * 4;
  const a = (src.data[o + 3] ?? 0) / 255;
  out[0] = (src.data[o] ?? 0) * a;
  out[1] = (src.data[o + 1] ?? 0) * a;
  out[2] = (src.data[o + 2] ?? 0) * a;
  out[3] = a;
};

/**
 * src 를 matrix(src px → dst px)로 변환해 dst 에 source-over 로 그린다.
 * dst 픽셀 중심을 역매핑해 premultiplied 쌍선형으로 표본한다. 원본 사각형 안만 그리고 이웃은 가장자리로 클램프한다.
 */
export const drawRasterTransformed = (
  dst: Raster,
  src: Raster,
  matrix: Matrix,
  clip?: ClipRect,
): void => {
  const inverse = invert(matrix);
  if (inverse === null) return;
  const corners = [
    apply(matrix, { x: 0, y: 0 }),
    apply(matrix, { x: src.width, y: 0 }),
    apply(matrix, { x: 0, y: src.height }),
    apply(matrix, { x: src.width, y: src.height }),
  ];
  const x0 = Math.max(
    0,
    Math.floor(Math.min(...corners.map((c) => c.x))),
    clip ? Math.floor(clip.x) : 0,
  );
  const y0 = Math.max(
    0,
    Math.floor(Math.min(...corners.map((c) => c.y))),
    clip ? Math.floor(clip.y) : 0,
  );
  const x1 = Math.min(
    dst.width,
    Math.ceil(Math.max(...corners.map((c) => c.x))),
    clip ? Math.ceil(clip.x + clip.width) : dst.width,
  );
  const y1 = Math.min(
    dst.height,
    Math.ceil(Math.max(...corners.map((c) => c.y))),
    clip ? Math.ceil(clip.y + clip.height) : dst.height,
  );
  const p00 = new Float64Array(4);
  const p10 = new Float64Array(4);
  const p01 = new Float64Array(4);
  const p11 = new Float64Array(4);
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      const s = apply(inverse, { x: x + 0.5, y: y + 0.5 });
      if (s.x < 0 || s.y < 0 || s.x > src.width || s.y > src.height) continue;
      const u = s.x - 0.5;
      const v = s.y - 0.5;
      const ix = Math.floor(u);
      const iy = Math.floor(v);
      const fx = u - ix;
      const fy = v - iy;
      samplePremul(src, ix, iy, p00);
      samplePremul(src, ix + 1, iy, p10);
      samplePremul(src, ix, iy + 1, p01);
      samplePremul(src, ix + 1, iy + 1, p11);
      const mix = (i: number): number =>
        (p00[i] ?? 0) * (1 - fx) * (1 - fy) +
        (p10[i] ?? 0) * fx * (1 - fy) +
        (p01[i] ?? 0) * (1 - fx) * fy +
        (p11[i] ?? 0) * fx * fy;
      const a = mix(3);
      if (a <= 0) continue;
      blendPixel(dst.data, (y * dst.width + x) * 4, mix(0) / a, mix(1) / a, mix(2) / a, a * 255);
    }
  }
};
